// BalanceContext.tsx
import React, { createContext, useCallback, useContext, useState } from "react";
import { balance as fetchBalanceAPI } from "../api/FetchAPI";
import { showAlert } from "../promptMessages/PromptMessages";
import { ALERTMSG } from "../lib/enums";

interface BalanceContextType {
  balances: Record<number, string>;
  fetchBalance: (
    partnerId: number,
    customColor?: string,
    force?: boolean,
  ) => Promise<void>;
  clearBalances: () => void;
}
const BalanceContext = createContext<BalanceContextType | undefined>(undefined);

export const BalanceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [balances, setBalances] = useState<Record<number, string>>({});

  const fetchBalance = useCallback(
    async (partnerId: number, customColor?: string, force = false) => {
      if (!partnerId) return;
      if (!force && balances[partnerId] !== undefined) return;

      const res = await fetchBalanceAPI(partnerId);
      if (res.respCode === 2000) {
        setBalances((prev) => ({
          ...prev,
          [partnerId]: res.balance?.toLocaleString() ?? "N/A",
        }));
      } else {
        showAlert(ALERTMSG.SOMEWENTWRONG, res.respMsg, "error", customColor);
        setBalances((prev) => ({ ...prev, [partnerId]: "N/A" }));
      }
    },
    [balances],
  );

  const clearBalances = useCallback(() => {
    setBalances({});
  }, []);

  return (
    <BalanceContext.Provider value={{ balances, fetchBalance, clearBalances }}>
      {children}
    </BalanceContext.Provider>
  );
};

export const useBalance = (): BalanceContextType => {
  const context = useContext(BalanceContext);
  if (!context) {
    throw new Error("useBalance must be used within a BalanceProvider");
  }

  return context;
};
